import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Leaf, Eye, EyeOff, ShieldCheck, AlertCircle, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { isSupabaseConfigured } from '@/services/supabase-client.service';

// ── Shared input style (matches practice-web Input component) ─────────────
const inputClass = cn(
    'w-full rounded-xl border border-neutral-200 dark:border-neutral-700',
    'bg-white dark:bg-neutral-800 px-4 py-3 text-sm',
    'text-neutral-900 dark:text-neutral-100',
    'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
    'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0 focus:border-brand-500',
    'transition-colors',
);

export default function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, error, clearError } = useAuth();

    const [email, setEmail]               = useState('');
    const [password, setPassword]         = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resetMode, setResetMode]       = useState(false);

    const configured  = isSupabaseConfigured();
    const timedOut    = (location.state as { timedOut?: boolean })?.timedOut;
    const from        = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
    const practiceUrl = (import.meta.env.VITE_PRACTICE_APP_URL as string) || 'https://praxis.getflowly.de';

    useEffect(() => {
        if (isAuthenticated) navigate(from, { replace: true });
    }, [isAuthenticated, navigate, from]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearError();
        setIsSubmitting(true);
        try {
            await login({ email, password });
            navigate(from, { replace: true });
        } catch {
            // error handled by context
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900 px-4">
            <div className="w-full max-w-md space-y-8">

                {/* ── Logo + Heading ── */}
                <div className="text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 shadow-lg">
                        <Leaf className="h-7 w-7 text-white" />
                    </div>
                    <h1 className="mt-4 text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                        flowly Pflege
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                        Melden Sie sich an, um fortzufahren
                    </p>
                </div>

                {/* ── App-Toggle ── */}
                <div className="flex rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800 p-1">
                    <a
                        href={`${practiceUrl}/login`}
                        className={cn(
                            'flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-colors',
                            'text-neutral-500 hover:text-brand-600 dark:hover:text-brand-400',
                        )}
                    >
                        <ShieldCheck className="h-4 w-4" />
                        Praxis
                    </a>
                    <span
                        className={cn(
                            'flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium cursor-default',
                            'bg-white dark:bg-neutral-700 shadow-sm text-brand-600 dark:text-brand-400',
                        )}
                    >
                        <Leaf className="h-4 w-4" />
                        Pflege
                    </span>
                </div>

                {/* ── Supabase not configured ── */}
                {!configured && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300">
                        Supabase nicht konfiguriert. Bitte{' '}
                        <code className="font-mono text-xs">.env.local</code> anlegen.
                    </div>
                )}

                {/* ── Session timeout warning ── */}
                {timedOut && (
                    <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                        <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                            Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.
                        </p>
                    </div>
                )}

                {/* ── Main content: Login form or Reset info ── */}
                {!resetMode ? (
                    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">

                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                                E-Mail
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                autoComplete="email"
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={inputClass}
                                placeholder="pfleger@einrichtung.de"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                                Passwort
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={cn(inputClass, 'pr-11')}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((p) => !p)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
                                    aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Auth error */}
                        {error && (
                            <div role="alert" aria-live="assertive" className="flex items-start gap-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
                                <AlertCircle className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />
                                <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
                            </div>
                        )}

                        <Button
                            type="submit"
                            className="w-full"
                            loading={isSubmitting}
                            disabled={isSubmitting || !email || !password || !configured}
                        >
                            Anmelden
                        </Button>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setResetMode(true)}
                                className="text-sm text-brand-600 hover:text-brand-700 hover:underline transition-colors"
                            >
                                Passwort vergessen?
                            </button>
                        </div>
                    </form>
                ) : (
                    /* ── Password reset info ── */
                    <div className="space-y-5">
                        <div className="flex items-center justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                                <Mail className="h-6 w-6 text-brand-600" />
                            </div>
                        </div>
                        <div className="rounded-xl border border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-brand-900/20 p-4 text-center">
                            <p className="font-medium text-brand-800 dark:text-brand-300">Passwort zurücksetzen</p>
                            <p className="mt-2 text-sm text-brand-700 dark:text-brand-400">
                                Bitte wenden Sie sich an Ihren Administrator oder schreiben Sie uns an{' '}
                                <a href="mailto:kontakt@getflowly.de" className="font-medium underline">
                                    kontakt@getflowly.de
                                </a>
                                .
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setResetMode(false)}
                            className="inline-flex w-full items-center justify-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 hover:underline transition-colors"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            Zurück zur Anmeldung
                        </button>
                    </div>
                )}

                {/* ── Footer ── */}
                <p className="text-center text-xs text-neutral-500 dark:text-neutral-500">
                    Nur für autorisiertes Pflegepersonal. Geschützt nach DSGVO.
                </p>

            </div>
        </div>
    );
}
